import React, { useEffect, useState } from "react";
import { PageContainer, PageHeader, Card, ConfirmDelete } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const empty = { title: "", department: "", location: "", description: "", status: "open", order: 0 };

export default function JobsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => setItems((await adminApi.get("/admin/jobs")).data);
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("Title required");
    setBusy(true);
    try {
      if (editing) await adminApi.put(`/admin/jobs/${editing.id}`, form);
      else await adminApi.post("/admin/jobs", form);
      toast.success(editing ? "Updated" : "Posted");
      setOpen(false);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await adminApi.delete(`/admin/jobs/${id}`);
    await load();
    toast.success("Deleted");
  };

  return (
    <PageContainer>
      <PageHeader title="Job Openings" subtitle="Roles displayed on the Careers section."
        action={<Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="text-white" style={{ background: "#1B6FE0" }} data-testid="job-new"><Plus size={14} weight="bold"/> New role</Button>} />
      <Card>
        <ul className="divide-y divide-slate-100" data-testid="job-list">
          {items.length === 0 && <li className="p-8 text-center text-[#6B7A8D]">No roles yet.</li>}
          {items.map((j) => (
            <li key={j.id} className="p-4 flex items-center gap-3" data-testid={`job-item-${j.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14.5px]" style={{ color: "#0F2A6E" }}>{j.title}</div>
                <div className="text-[12px] text-[#6B7A8D]">{j.department} · {j.location} · <span className={j.status === "open" ? "text-emerald-600" : "text-slate-500"}>{j.status}</span></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => { setEditing(j); setForm({ ...empty, ...j }); setOpen(true); }} data-testid={`job-edit-${j.id}`}><PencilSimple size={13} weight="bold"/></Button>
              <ConfirmDelete title="Delete this role?" description={`"${j.title}" will no longer appear under Open roles.`} onConfirm={() => del(j.id)} testId={`job-del-${j.id}`}>
                <Button variant="outline" size="sm" className="text-[#1B6FE0] hover:bg-blue-50 hover:text-[#1B6FE0]" data-testid={`job-del-${j.id}`}><Trash size={13} weight="bold"/></Button>
              </ConfirmDelete>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="job-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit role" : "New role"}</DialogTitle>
            <DialogDescription>Open roles appear on the Careers section. Set status to closed to hide.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div className="md:col-span-3"><Label>Title</Label><Input className="mt-1.5" value={form.title} onChange={(e) => set("title", e.target.value)} required data-testid="job-title"/></div>
              <div><Label>Department</Label><Input className="mt-1.5" value={form.department} onChange={(e) => set("department", e.target.value)}/></div>
              <div><Label>Location</Label><Input className="mt-1.5" value={form.location} onChange={(e) => set("location", e.target.value)}/></div>
              <div><Label>Order</Label><Input type="number" className="mt-1.5" value={form.order} onChange={(e) => set("order", parseInt(e.target.value || "0"))}/></div>
            </div>
            <div><Label>Description</Label><Textarea rows={4} className="mt-1.5" value={form.description} onChange={(e) => set("description", e.target.value)}/></div>
            <div className="flex items-center gap-3">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-[160px]"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ background: "#1B6FE0" }} data-testid="job-save">{busy ? "Saving…" : (editing ? "Update" : "Create")}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
